import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useAuth } from "../../context/AuthContext";
import { useSearchParams } from "react-router-dom";
import { API_URLS, BACKEND_URL } from "../../config";
import { apiRequest } from "../../utils/apiRequest";
import { DEFAULT_CURRENCY_RATES } from "../../utils/currencyRates";
import formatDate from "../../utils/formatDate";
import AccessDenied from "../../Components/AccessDenied";
import { useToast } from "../../Components/Toast";
import {
  FiEdit, FiPlus, FiEye, FiTrash2, FiChevronUp, FiChevronDown, FiRefreshCw, FiCheck, FiX, FiUploadCloud, FiMapPin, FiUsers, FiAward, FiDollarSign, FiUpload, FiPaperclip
} from "react-icons/fi";
import { FaFile, FaHelmetSafety } from "react-icons/fa6";
import { FiUserCheck } from "react-icons/fi";
import MasterUploadModal from "../../Components/MasterUploadModal";
import PhoneField from "../../Components/PhoneField";
import Competency from "../../Components/Competency";
import ClientSpecificApproval from "../../Components/ClientSpecificApproval";
import Select, { createFilter } from "react-select";
import { Country, State, City } from "country-state-city";
import "./Inspectors.css";
import FilterPanel, { FilterField, filterSelectStyles } from "../../Components/FilterPanel";

export const DEGREE_OPTIONS = ["B.E.", "B.Tech", "M.E.", "M.Tech", "Diploma" , "B.Sc" , "M.Sc"];
export const BRANCH_OPTIONS = ["Mechanical Engineering", "Electrical Engineering", "Instrumentation Engineering", "Electronics Engineering", "Civil Engineering", "Metallurgical Engineering", "Materials Engineering", "Welding Engineering", "Production Engineering", "Chemical Engineering", "Industrial Engineering", "Marine Engineering", "Petroleum Engineering", "Process Engineering", "Power Engineering", "Structural Engineering", "Automation Engineering", "Mechatronics Engineering", "Mining Engineering" , "Aerospace Engineering" , "Computer Engineering"];

// Location dropdowns match from the start of the name, so "oman" finds Oman, not Romania.
const matchFromStart = createFilter({ matchFrom: "start" });

/* ── constants ──────────────────────────────────────────────────── */
const EMPTY_FORM = {
  inspectorName: "",
  email: "",
  phone: "",
  alternateEmail: "",
  alternatePhone: "",
  cvUrl: "",
  inspectorSource: "Direct",
  age: "",
  experience: "",
  degree: "",
  branch: "",
  standardRate: { currency: "INR", amount: "" },
  country: "",
  state: "",
  city: "",
  competency: {},
  certificates: [],
  clientApprovals: { clients: [], otherClients: [] },
  bankIndia: {
    beneficiaryName: "",
    bankName: "",
    branch: "",
    bankAddress: "",
    accountNo: "",
    ifscCode: "",
  },
  bankOther: {
    beneficiaryName: "",
    beneficiaryAddress: "",
    bankName: "",
    bankAddress: "",
    ibanNo: "",
    swiftCode: "",
  },
  contactPersons: [],
  profileImageUrl: "",
  profileImageFile: null,
  createdBy: "", // only ever set/sent by global super admins — see the Created By picker
};

const EMPTY_CERT = { certificateId: "", expiryDate: "", fileUrl: "" };
const EMPTY_CONTACT = { personName: "", email: "", mobileNumber: "" };

const initials = (name = "") =>
  name.trim().split(" ").slice(0, 2).map((w) => w[0]?.toUpperCase() || "").join("");

/* ── react-select styles ────────────────────────────────────────── */

/* ══════════════════════════════════════════════════════════════════ */
const Inspectors = () => {
  const { token, user } = useAuth();
  const toast = useToast();
  const [searchParams] = useSearchParams();

  /* ── list state ── */
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);

  /* ── selection ── */
  const [selectedIds, setSelectedIds] = useState([]);

  /* ── filters ── */
  const [search, setSearch] = useState("");
  const [fCountry, setFCountry] = useState("");
  const [fState, setFState] = useState("");
  const [fCity, setFCity] = useState("");
  const [fDegree, setFDegree] = useState("");
  const [fBranch, setFBranch] = useState("");
  const [fCertificate, setFCertificate] = useState("");
  const [fProduct, setFProduct] = useState("");
  const [isTableCollapsed, setIsTableCollapsed] = useState(false);

  /* ── modal ── */
  const [modal, setModal] = useState(null); // "add" | "edit" | "view"
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  /* ── "Created By" picker — global super admin only, Add modal only ── */
  const [creators, setCreators] = useState([]);
  useEffect(() => {
    if (user?.role !== 'global_superadmin') return;
    apiRequest(`${API_URLS.INSPECTORS}/creators`, {}, token).then((res) => {
      if (res?.ok) setCreators(res.data.rows || []);
    });
  }, [user?.role, token]);

  /* ── section collapse ── */
  const [secBasic, setSecBasic] = useState(true);
  const [secLocation, setSecLocation] = useState(true);
  const [secQual, setSecQual] = useState(true);
  const [secBank, setSecBank] = useState(true);
  const [secContacts, setSecContacts] = useState(true);

  /* ── inline-add states ── */
  const [certFormOpen, setCertFormOpen] = useState(false);
  const [certInput, setCertInput] = useState(EMPTY_CERT);
  const [contactFormOpen, setContactFormOpen] = useState(false);
  const [contactInput, setContactInput] = useState(EMPTY_CONTACT);
  const [profilePreview, setProfilePreview] = useState(null);

  /* ── certificate / product options (also used by filter dropdowns) ── */
  const [certificateOptions, setCertificateOptions] = useState([]);
  const [productOptions, setProductOptions] = useState([]);

  /* ── country / state / city ── */
  const allCountries = useMemo(() => Country.getAllCountries(), []);

  const selectedCountryIso = useMemo(() => {
    const c = allCountries.find((c) => c.name === formData.country);
    return c ? c.isoCode : "";
  }, [allCountries, formData.country]);

  const stateOptions = useMemo(() => {
    if (!selectedCountryIso) return [];
    return State.getStatesOfCountry(selectedCountryIso);
  }, [selectedCountryIso]);

  const selectedStateIso = useMemo(() => {
    const s = stateOptions.find((s) => s.name === formData.state);
    return s ? s.isoCode : "";
  }, [stateOptions, formData.state]);

  const cityOptions = useMemo(() => {
    if (!selectedCountryIso || !selectedStateIso) return [];
    return City.getCitiesOfState(selectedCountryIso, selectedStateIso);
  }, [selectedCountryIso, selectedStateIso]);

  /* filter-panel options — states/cities for whatever country/state is currently picked */
  const isoOfCountry = (name) => allCountries.find((c) => c.name === name)?.isoCode || "";
  const statesOf = (country) => {
    const iso = isoOfCountry(country);
    return iso ? State.getStatesOfCountry(iso) : [];
  };
  const citiesOf = (country, state) => {
    const iso = isoOfCountry(country);
    const st = statesOf(country).find((s) => s.name === state);
    return iso && st ? City.getCitiesOfState(iso, st.isoCode) : [];
  };

  /* ── India bank toggle ── */
  const isIndia = formData.country === "India";

  /* ── show contact persons only for Agency ── */
  const isAgency = formData.inspectorSource === "Agency";

  /* ── fetch certificate options ── */
  const fetchCertOptions = useCallback(async () => {
    if (!token) return;
    const result = await apiRequest(`${API_URLS.CERTIFICATES}?limit=1000`, {}, token);
    if (result?.ok) setCertificateOptions(result.data.rows || []);
  }, [token]);

  /* ── fetch product options (for the Product filter dropdown) ── */
  const fetchProductOptions = useCallback(async () => {
    if (!token) return;
    const result = await apiRequest(`${API_URLS.PRODUCTS}?limit=1000`, {}, token);
    if (result?.ok) setProductOptions(result.data.rows || []);
  }, [token]);

  /* ── fetch list ── */
  const fetchRows = useCallback(async () => {
    if (!token) { setLoading(false); return; }
    setLoading(true);
    const params = new URLSearchParams({ page, limit, mine: "true" });
    if (search) params.set("search", search);
    if (fCountry) params.set("country", fCountry);
    if (fState) params.set("state", fState);
    if (fCity) params.set("city", fCity);
    if (fDegree) params.set("degree", fDegree);
    if (fBranch) params.set("branch", fBranch);
    if (fCertificate) params.set("certificateId", fCertificate);
    if (fProduct) params.set("productId", fProduct);

    const result = await apiRequest(`${API_URLS.INSPECTORS}?${params}`, {}, token);
    if (!result) return;
    if (result.denied) { setDenied(result); setLoading(false); return; }
    if (result.ok) {
      setRows(result.data.rows || []);
      setTotal(result.data.total || 0);
      setSelectedIds([]);
    }
    setLoading(false);
  }, [token, page, search, fCountry, fState, fCity, fDegree, fBranch, fCertificate, fProduct, limit]);

  useEffect(() => { fetchRows(); }, [fetchRows]);
  useEffect(() => { fetchCertOptions(); fetchProductOptions(); }, [fetchCertOptions, fetchProductOptions]);

  // Auto-open edit modal if ?inspectorId= is in URL (e.g. navigated from Manday page)
  useEffect(() => {
    const inspectorId = searchParams.get('inspectorId');
    if (inspectorId && token) {
      const autoOpen = async () => {
        const res = await apiRequest(`${API_URLS.INSPECTORS}/${inspectorId}`, {}, token);
        if (res?.ok) {
          const loaded = { ...EMPTY_FORM, ...res.data, createdBy: res.data.createdBy ? String(res.data.createdBy) : "" };
          setFormData(loaded);
          initialFormSnapshotRef.current = JSON.stringify(loaded);
          setCertFormOpen(false); setContactFormOpen(false);
          setProfilePreview(res.data.profileImageUrl ? `${BACKEND_URL}${res.data.profileImageUrl}` : null);
          resetSections();
          setModal('edit');
          // Pre-select this inspector in the list
          setSelectedIds([inspectorId]);
        }
      };
      autoOpen();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  /* ── selection ── */
  const toggleSelectRow = (id) =>
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  const toggleSelectAll = () =>
    setSelectedIds(selectedIds.length === rows.length && rows.length > 0 ? [] : rows.map((r) => r.id));

  /* ── modal helpers ── */
  const resetSections = () => {
    setSecBasic(true); setSecLocation(true); setSecQual(true);
    setSecBank(true); setSecContacts(true);
  };

  const initialFormSnapshotRef = useRef(null);
  const openAdd = () => {
    setFormData(EMPTY_FORM);
    initialFormSnapshotRef.current = JSON.stringify(EMPTY_FORM);
    setCertInput(EMPTY_CERT); setCertFormOpen(false);
    setContactInput(EMPTY_CONTACT); setContactFormOpen(false);
    setProfilePreview(null);
    resetSections();
    setModal("add");
  };

  const openEdit = async () => {
    if (selectedIds.length !== 1) return;
    const res = await apiRequest(`${API_URLS.INSPECTORS}/${selectedIds[0]}`, {}, token);
    if (res?.ok) {
      const loaded = { ...EMPTY_FORM, ...res.data, createdBy: res.data.createdBy ? String(res.data.createdBy) : "" };
      setFormData(loaded);
      initialFormSnapshotRef.current = JSON.stringify(loaded);
      setCertFormOpen(false); setContactFormOpen(false);
      setProfilePreview(res.data.profileImageUrl ? `${BACKEND_URL}${res.data.profileImageUrl}` : null);
      resetSections();
      setModal("edit");
    }
  };

  const openView = async () => {
    if (selectedIds.length !== 1) return;
    const res = await apiRequest(`${API_URLS.INSPECTORS}/${selectedIds[0]}`, {}, token);
    if (res?.ok) {
      setFormData({ ...EMPTY_FORM, ...res.data });
      setCertFormOpen(false); setContactFormOpen(false);
      setProfilePreview(res.data.profileImageUrl ? `${BACKEND_URL}${res.data.profileImageUrl}` : null);
      resetSections();
      setModal("view");
    }
  };

  const closeModal = () => {
    setModal(null);
    setFormData(EMPTY_FORM);
    setCertInput(EMPTY_CERT); setCertFormOpen(false);
    setContactInput(EMPTY_CONTACT); setContactFormOpen(false);
    setProfilePreview(null);
  };

  // Unsaved-changes guard — covers the main form plus any open-but-unsaved certificate/contact entry
  const isFormDirty = () => {
    if (modal === "view") return false;
    const certDraftPending = certFormOpen && !!certInput.certificateId;
    const contactDraftPending = contactFormOpen && !!(contactInput.personName || contactInput.email || contactInput.mobileNumber);
    const formChanged = initialFormSnapshotRef.current !== null && JSON.stringify(formData) !== initialFormSnapshotRef.current;
    return certDraftPending || contactDraftPending || formChanged;
  };
  const requestCloseModal = () => {
    if (isFormDirty()) {
      toast.confirm(
        "You have unsaved changes. Do you want to save before closing?",
        () => handleSave(),
        () => closeModal()
      );
    } else {
      closeModal();
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((f) => ({ ...f, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData((f) => ({ ...f, profileImageFile: file }));
      setProfilePreview(URL.createObjectURL(file));
    }
  };

  const handleNestedChange = (section, field, value) => {
    setFormData((f) => ({
      ...f,
      [section]: { ...f[section], [field]: value },
    }));
  };

  /* ── certificate logic ── */
  const handleAddCert = () => {
    if (!certInput.certificateId) { toast.info("Please select a certificate."); return; }
    setFormData((f) => ({ ...f, certificates: [...f.certificates, { ...certInput }] }));
    setCertInput(EMPTY_CERT);
    setCertFormOpen(false);
  };
  const handleRemoveCert = (idx) =>
    setFormData((f) => ({ ...f, certificates: f.certificates.filter((_, i) => i !== idx) }));
  const handleCertFileChange = (idx, file) =>
    setFormData((f) => ({
      ...f,
      certificates: f.certificates.map((c, i) => (i === idx ? { ...c, certFile: file } : c)),
    }));
  const handleClearCertFile = (idx) =>
    setFormData((f) => ({
      ...f,
      certificates: f.certificates.map((c, i) => {
        if (i !== idx) return c;
        const { certFile: _f, ...rest } = c;
        return rest;
      }),
    }));

  /* ── contact person logic ── */
  const handleAddContact = () => {
    if (!contactInput.personName || !contactInput.email) {
      toast.info("Name and email are required."); return;
    }
    if (formData.contactPersons.some((c) => c.email.toLowerCase() === contactInput.email.toLowerCase())) {
      toast.info("This email is already added."); return;
    }
    setFormData((f) => ({ ...f, contactPersons: [...f.contactPersons, { ...contactInput }] }));
    setContactInput(EMPTY_CONTACT);
    setContactFormOpen(false);
  };
  const handleRemoveContact = (idx) =>
    setFormData((f) => ({ ...f, contactPersons: f.contactPersons.filter((_, i) => i !== idx) }));

  /* ── save ── */
  const handleSave = async (e) => {
    e?.preventDefault();
    if (!formData.inspectorName) { toast.warning("Inspector Name is required!"); return; }
    if (!formData.email) { toast.warning("Email is required!"); return; }
    if (!formData.country) { toast.warning("Country is required!"); return; }
    setSaving(true);
    const isEdit = modal === "edit";

    // Build cert ordering first so we can validate names before hitting the API.
    // Backend matches certFiles[i] to certs[i] by index — certs with files go first.
    const certsWithFile = (formData.certificates || []).filter(c => c.certFile);
    const certsWithoutFile = (formData.certificates || []).filter(c => !c.certFile);

    // Guard: every cert that has a new file must resolve to a known certificate name.
    if (certsWithFile.length > 0) {
      if (certificateOptions.length === 0) {
        toast.warning('The certificate list is still loading. Please wait a moment and try again.');
        setSaving(false);
        return;
      }
      const badIdx = certsWithFile.findIndex(
        c => !certificateOptions.find(o => String(o.id) === String(c.certificateId))?.name
      );
      if (badIdx !== -1) {
        toast.warning(
          `Certificate ${badIdx + 1} cannot be matched to a valid certificate type. ` +
          `It may have been deleted from the master list. Please remove it and re-add the correct one.`
        );
        setSaving(false);
        return;
      }
    }

    const url = isEdit ? `${API_URLS.INSPECTORS}/${selectedIds[0]}` : API_URLS.INSPECTORS;
    const submitData = new FormData();
    // Strip certFile (binary) before sending — backend resolves cert name from DB by certificateId
    const orderedCerts = [...certsWithFile, ...certsWithoutFile].map(({ certFile, _certName, ...rest }) => rest);
    const { profileImageFile, cvFile, ...restForm } = formData;
    const payload = { ...restForm, certificates: orderedCerts };
    submitData.append("data", JSON.stringify(payload));
    if (profileImageFile) submitData.append("profileImage", profileImageFile);
    if (cvFile) submitData.append("cv", cvFile);
    certsWithFile.forEach(c => submitData.append("certFiles", c.certFile));

    const result = await apiRequest(url, { method: isEdit ? "PUT" : "POST", body: submitData, isFormData: true }, token);
    setSaving(false);
    if (result?.ok) { toast.success("Saved successfully!"); closeModal(); fetchRows(); }
    else toast.error(result?.data?.message || "Failed to save.");
  };

  /* ── delete ── */
  const handleDelete = () => {
    if (!selectedIds.length) return;
    const msg = selectedIds.length === 1
      ? "Delete this inspector?"
      : `Delete ${selectedIds.length} selected inspectors?`;
    toast.deleteConfirm(msg, async () => {
      setLoading(true);
      let ok = 0;
      for (const id of selectedIds) {
        const res = await apiRequest(`${API_URLS.INSPECTORS}/${id}`, { method: "DELETE" }, token);
        if (res?.ok) ok++;
      }
      setLoading(false);
      if (ok > 0) { toast.success(`Deleted ${ok} item(s).`); fetchRows(); }
      else toast.error("Delete failed.");
    });
  };

  /* ── export ── */
  const handleExport = async () => {
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (fCountry) params.set("country", fCountry);
    if (fState) params.set("state", fState);
    if (fCity) params.set("city", fCity);
    if (fDegree) params.set("degree", fDegree);
    if (fBranch) params.set("branch", fBranch);
    if (fCertificate) params.set("certificateId", fCertificate);
    if (fProduct) params.set("productId", fProduct);
    const res = await fetch(`${API_URLS.INSPECTORS}/export?${params}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) {
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `Inspectors_${Date.now()}.xlsx`; a.click();
    } else toast.error("Export failed.");
  };

  const pages = Math.ceil(total / limit);
  const isView = modal === "view";
  const isAdd = modal === "add";

  if (denied) return (
    <div className="vendor-page">
      <AccessDenied message={denied.message} required={denied.required} />
    </div>
  );

  /* ══════════════ RENDER ══════════════ */
  return (
    <div className="vendor-page">

      {/* ── Action Bar ── */}
      <div className="vendor-action-bar">
        <button className="new-user-btn" onClick={openAdd}>
          <FiPlus /> New Inspector
        </button>
        <div style={{ width: 1, height: 24, background: "#ccc", margin: "0 8px" }} />
        <button className="action-btn view-btn" disabled={selectedIds.length !== 1} onClick={openView}>
          <FiEye /> View
        </button>
        <button className="action-btn edit-btn" disabled={selectedIds.length !== 1} onClick={openEdit}>
          <FiEdit /> Edit
        </button>
        {['global_superadmin', 'company_admin', 'project_manager'].includes(user?.role) && (
          <button className="action-btn delete-btn" disabled={selectedIds.length === 0} onClick={handleDelete}>
            <FiTrash2 /> Delete
          </button>
        )}
        {['global_superadmin', 'company_admin'].includes(user?.role) && (
          <>
            <div style={{ width: 1, height: 24, background: "#ccc", margin: "0 8px" }} />
            <button className="action-btn export-btn" onClick={handleExport}>
              <FiRefreshCw size={13} /> Export
            </button>
            <button className="action-btn upload-btn" onClick={() => setIsUploadOpen(true)}>
              <FiUploadCloud /> Upload
            </button>
          </>
        )}
        <FilterPanel
          title="Filter inspectors"
          width={560}
          columns={2}
          values={{ search, country: fCountry, state: fState, city: fCity, degree: fDegree, branch: fBranch, product: fProduct, certificate: fCertificate }}
          emptyValues={{ search: "", country: "", state: "", city: "", degree: "", branch: "", product: "", certificate: "" }}
          onChange={(v) => { setSearch(v.search); setFCountry(v.country); setFState(v.state); setFCity(v.city); setFDegree(v.degree); setFBranch(v.branch); setFProduct(v.product); setFCertificate(v.certificate); setPage(1); }}
        >
          {(filters, set) => (
            <>
              <FilterField label="Search" wide>
                <input autoFocus placeholder="Search inspectors" value={filters.search}
                  onChange={(e) => set({ search: e.target.value })} />
              </FilterField>
              <FilterField label="Country">
                <Select
                  options={allCountries.map((c) => ({ value: c.name, label: c.name }))}
                  filterOption={matchFromStart}
                  value={filters.country ? { value: filters.country, label: filters.country } : null}
                  onChange={(s) => set({ country: s ? s.value : "", state: "", city: "" })}
                  placeholder="All Countries"
                  isClearable
                  styles={filterSelectStyles}
                />
              </FilterField>
              <FilterField label="State">
                <Select
                  options={statesOf(filters.country).map((s) => ({ value: s.name, label: s.name }))}
                  filterOption={matchFromStart}
                  value={filters.state ? { value: filters.state, label: filters.state } : null}
                  onChange={(s) => set({ state: s ? s.value : "", city: "" })}
                  placeholder="All States"
                  isDisabled={!filters.country}
                  isClearable
                  styles={filterSelectStyles}
                />
              </FilterField>
              <FilterField label="City">
                <Select
                  options={citiesOf(filters.country, filters.state).map((c) => ({ value: c.name, label: c.name }))}
                  filterOption={matchFromStart}
                  value={filters.city ? { value: filters.city, label: filters.city } : null}
                  onChange={(s) => set({ city: s ? s.value : "" })}
                  placeholder="All Cities"
                  isDisabled={!filters.state}
                  isClearable
                  styles={filterSelectStyles}
                />
              </FilterField>
              <FilterField label="Degree">
                <Select
                  options={DEGREE_OPTIONS.map((d) => ({ value: d, label: d }))}
                  value={filters.degree ? { value: filters.degree, label: filters.degree } : null}
                  onChange={(s) => set({ degree: s ? s.value : "" })}
                  placeholder="All Degrees"
                  isClearable
                  styles={filterSelectStyles}
                />
              </FilterField>
              <FilterField label="Branch">
                <Select
                  options={BRANCH_OPTIONS.map((b) => ({ value: b, label: b }))}
                  value={filters.branch ? { value: filters.branch, label: filters.branch } : null}
                  onChange={(s) => set({ branch: s ? s.value : "" })}
                  placeholder="All Branches"
                  isClearable
                  styles={filterSelectStyles}
                />
              </FilterField>
              <FilterField label="Product">
                <Select
                  options={productOptions.map((p) => ({ value: p.id, label: p.name }))}
                  value={filters.product ? { value: filters.product, label: productOptions.find((p) => p.id === filters.product)?.name || "" } : null}
                  onChange={(s) => set({ product: s ? s.value : "" })}
                  placeholder="All Products"
                  isClearable
                  styles={filterSelectStyles}
                />
              </FilterField>
              <FilterField label="Certificate">
                <Select
                  options={certificateOptions.map((c) => ({ value: c.id, label: c.name }))}
                  value={filters.certificate ? { value: filters.certificate, label: certificateOptions.find((c) => c.id === filters.certificate)?.name || "" } : null}
                  onChange={(s) => set({ certificate: s ? s.value : "" })}
                  placeholder="All Certificates"
                  isClearable
                  styles={filterSelectStyles}
                />
              </FilterField>
            </>
          )}
        </FilterPanel>
      </div>

      {/* ── Table Card ── */}
      <div className="management-card">
        <div className="management-header" onClick={() => setIsTableCollapsed((v) => !v)}>
          <div className="header-title">
            <FaHelmetSafety size={18} />
            <h2>Inspector Master</h2>
            <span className="vendor-badge">{total}</span>
          </div>
          <div>{isTableCollapsed ? <FiChevronDown /> : <FiChevronUp />}</div>
        </div>

        {!isTableCollapsed && (
          <div className="vendor-table-container">
            {loading && rows.length === 0 ? (
              <div style={{ padding: 40, textAlign: "center", color: "#888" }}>Loading...</div>
            ) : (
              <table className="vendor-table">
                <thead>
                  <tr>
                    <th style={{ width: 40 }}>
                      <input
                        type="checkbox"
                        checked={rows.length > 0 && selectedIds.length === rows.length}
                        onChange={toggleSelectAll}
                      />
                    </th>
                    <th style={{ width: 60 }}>SR. NO.</th>
                    <th>INSPECTOR NAME</th>
                    <th>INSPECTOR SOURCE</th>
                    <th>EMAIL</th>
                    <th>PHONE</th>
                    <th>COUNTRY</th>
                    <th>STATE</th>
                    <th>CITY</th>
                    <th>CREATED BY</th>
                    <th style={{ textAlign: 'center' }}>DOCS</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr><td colSpan={10} style={{ textAlign: "center", padding: 40, color: "#888" }}>No inspectors found</td></tr>
                  ) : rows.map((r, i) => (
                    <tr
                      key={r.id || i}
                      className={selectedIds.includes(r.id) ? "selected" : ""}
                      onClick={() => toggleSelectRow(r.id)}
                    >
                      <td onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(r.id)}
                          onChange={() => toggleSelectRow(r.id)}
                        />
                      </td>
                      <td>{r.srNo}</td>
                      <td>{r.inspectorName}</td>
                      <td>
                        <span className={`insp-type-pill ${r.inspectorSource === "Agency" ? "insp-type-agency" : "insp-type-direct"}`}>
                          {r.inspectorSource || "Direct"}
                        </span>
                      </td>
                      <td style={{ color: "#555" }}>{r.email}</td>
                      <td>{r.phone || "—"}</td>
                      <td>{r.country}</td>
                      <td>{r.state}</td>
                      <td>{r.city}</td>
                      <td>{r.createdBy || '—'}</td>
                      <td style={{ textAlign: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                          {r.cvUrl
                            ? <a href={r.cvUrl} target="_blank" rel="noopener noreferrer" title="CV uploaded" onClick={e => e.stopPropagation()} style={{ color: '#437cf0', display: 'flex' }}><FiPaperclip size={15} /></a>
                            : <span title="No CV uploaded" style={{ color: '#ccc', display: 'flex' }}><FiPaperclip size={15} /></span>
                          }
                          {(r.certificates && r.certificates.length > 0)
                            ? <span title={`${r.certificates.length} certificate(s) uploaded`} style={{ color: '#28a745', display: 'flex' }}><FaFile size={15} /></span>
                            : <span title="No certificates uploaded" style={{ color: '#ccc', display: 'flex' }}><FaFile size={15} /></span>
                          }
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {total > 0 && (
              <div className="cust-pagination">
                <select
                  value={limit}
                  onChange={e => { setLimit(Number(e.target.value)); setPage(1); }}
                  style={{ padding: '4px 8px', border: '1px solid #ddd', borderRadius: 4, fontSize: 13, cursor: 'pointer' }}
                >
                  {[25, 50, 75, 100].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
                <span className="cust-page-info">Page {page} of {Math.max(1, pages)}</span>
                <button className="cust-btn" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>Prev</button>
                <button className="cust-btn" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Next</button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ══════════════ MODAL ══════════════ */}
      {modal && (
        <div className="cust-overlay" onClick={requestCloseModal}>
          <div className="insp-modal" onClick={(e) => e.stopPropagation()}>

            {/* Modal Header */}
            <div className="cust-modal-header">
              <span className="cust-modal-title">
                <FiUserCheck style={{ marginRight: 8 }} />
                {modal.toUpperCase()} INSPECTOR
              </span>
              <button className="cust-modal-close" onClick={requestCloseModal}><FiX size={16} /></button>
            </div>

            {/* Modal Body */}
            <div className="insp-modal-body">

              {/* ── Row 1: Basic + Location ── */}
              <div className="insp-row-wrap">

                {/* Basic Information */}
                <div className="sub-card" style={{ flex: 1 }}>
                  <div className={`sub-card-header ${secBasic ? "expanded" : ""}`} onClick={() => setSecBasic((v) => !v)}>
                    <h2><FiUserCheck /> BASIC INFORMATION</h2>
                    {secBasic ? <FiChevronUp /> : <FiChevronDown />}
                  </div>
                  {secBasic && (
                    <div className="sub-card-content">
                      <div className="form-grid">
                        <div className="cust-fg-full">
                          <label className="cust-label">INSPECTOR NAME *</label>
                          <input className="cust-input" name="inspectorName" value={formData.inspectorName} onChange={handleFormChange} required disabled={isView} placeholder="John Smith" />
                        </div>
                        <div>
                          <label className="cust-label">EMAIL *</label>
                          <input className="cust-input" name="email" type="email" value={formData.email} onChange={handleFormChange} required disabled={isView} placeholder="john@example.com" />
                        </div>
                        <div>
                          <PhoneField
                            label="PHONE"
                            value={formData.phone}
                            onChange={(val) => setFormData((f) => ({ ...f, phone: val }))}
                            disabled={isView}
                          />
                        </div>
                        <div>
                          <label className="cust-label">ALTERNATE EMAIL</label>
                          <input className="cust-input" name="alternateEmail" type="email" value={formData.alternateEmail} onChange={handleFormChange} disabled={isView} placeholder="alternate@example.com" />
                        </div>
                        <div>
                          <PhoneField
                            label="ALTERNATE PHONE"
                            value={formData.alternatePhone}
                            onChange={(val) => setFormData((f) => ({ ...f, alternatePhone: val }))}
                            disabled={isView}
                          />
                        </div>
                        <div>
                          <label className="cust-label">INSPECTOR SOURCE</label>
                          <select className="cust-input" name="inspectorSource" value={formData.inspectorSource} onChange={handleFormChange} disabled={isView}>
                            <option value="Direct">Direct</option>
                            <option value="Agency">Agency</option>
                          </select>
                        </div>
                        <div>
                          <label className="cust-label">AGE</label>
                          <input className="cust-input" name="age" type="number" value={formData.age} onChange={handleFormChange} disabled={isView} placeholder="e.g. 35" min="1" max="100" />
                        </div>
                        <div>
                          <label className="cust-label">EXPERIENCE (YEARS)</label>
                          <select className="cust-input" name="experience" value={formData.experience} onChange={handleFormChange} disabled={isView}>
                            <option value="">Select experience</option>
                            {Array.from({ length: 30 }, (_, i) => i + 1).map(num => (
                              <option key={num} value={String(num)}>{num}</option>
                            ))}
                            <option value="30+">30+</option>
                          </select>
                        </div>
                        <div>
                          <label className="cust-label">DEGREE</label>
                          <select className="cust-input" name="degree" value={formData.degree} onChange={handleFormChange} disabled={isView}>
                            <option value="">Select degree</option>
                            {DEGREE_OPTIONS.map((d) => (
                              <option key={d} value={d}>{d}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="cust-label">BRANCH</label>
                          <select className="cust-input" name="branch" value={formData.branch} onChange={handleFormChange} disabled={isView}>
                            <option value="">Select branch</option>
                            {BRANCH_OPTIONS.map((b) => (
                              <option key={b} value={b}>{b}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="cust-label">STANDARD RATE</label>
                          <div style={{ display: "flex", gap: 6 }}>
                            <select
                              className="cust-input"
                              style={{ flex: "0 0 80px" }}
                              value={formData.standardRate?.currency || "INR"}
                              onChange={(e) => handleNestedChange("standardRate", "currency", e.target.value)}
                              disabled={isView}
                            >
                              {Object.keys(DEFAULT_CURRENCY_RATES).map((code) => (
                                <option key={code} value={code}>{code}</option>
                              ))}
                            </select>
                            <input
                              className="cust-input"
                              type="number"
                              style={{ flex: 1 }}
                              value={formData.standardRate?.amount ?? ""}
                              onChange={(e) => handleNestedChange("standardRate", "amount", e.target.value)}
                              disabled={isView}
                              placeholder="0.00"
                              min="0"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="cust-label">CV / RESUME</label>
                          {isView ? (
                            formData.cvUrl
                              ? <a href={formData.cvUrl} target="_blank" rel="noopener noreferrer" className="insp-file-link"><FiUpload size={12} /> View CV</a>
                              : <span style={{ fontSize: 12, color: "#aaa" }}>No CV uploaded</span>
                          ) : (
                            <div className="insp-cv-upload-wrap">
                              {formData.cvUrl && !formData.cvFile && (
                                <a href={formData.cvUrl} target="_blank" rel="noopener noreferrer" className="insp-file-link">
                                  <FiUpload size={12} /> View Current CV
                                </a>
                              )}
                              {formData.cvFile && (
                                <span className="insp-cv-selected">
                                  <FiCheck size={11} /> {formData.cvFile.name}
                                </span>
                              )}
                              <label htmlFor="insp-cv-upload" className="insp-cv-btn">
                                <FiUploadCloud size={12} />
                                {formData.cvUrl && !formData.cvFile ? "Replace CV" : "Upload CV"}
                              </label>
                              <input
                                id="insp-cv-upload"
                                type="file"
                                accept=".pdf,.doc,.docx"
                                style={{ display: "none" }}
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) setFormData((f) => ({ ...f, cvFile: file }));
                                }}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Location Information */}
                <div className="sub-card" style={{ flex: 1 }}>
                  <div className={`sub-card-header ${secLocation ? "expanded" : ""}`} onClick={() => setSecLocation((v) => !v)}>
                    <h2><FiMapPin /> OTHER INFORMATION</h2>
                    {secLocation ? <FiChevronUp /> : <FiChevronDown />}
                  </div>
                  {secLocation && (
                    <div className="sub-card-content">
                      <div className="form-grid">
                        <div>
                          <label className="cust-label">COUNTRY *</label>
                          {isView ? (
                            <input className="cust-input" value={formData.country || ""} disabled />
                          ) : (
                            <Select
                              options={allCountries.map((c) => ({ value: c.name, label: c.name }))}
                              filterOption={matchFromStart}
                              value={formData.country ? { value: formData.country, label: formData.country } : null}
                              onChange={(s) => setFormData((f) => ({ ...f, country: s ? s.value : "", state: "", city: "" }))}
                              placeholder="Select country"
                              isClearable
                              styles={filterSelectStyles}
                            />
                          )}
                        </div>
                        <div>
                          <label className="cust-label">STATE</label>
                          {isView ? (
                            <input className="cust-input" value={formData.state || ""} disabled />
                          ) : (
                            <Select
                              options={stateOptions.map((s) => ({ value: s.name, label: s.name }))}
                              filterOption={matchFromStart}
                              value={formData.state ? { value: formData.state, label: formData.state } : null}
                              onChange={(s) => setFormData((f) => ({ ...f, state: s ? s.value : "", city: "" }))}
                              placeholder="Select state"
                              isDisabled={!formData.country}
                              isClearable
                              styles={filterSelectStyles}
                            />
                          )}
                        </div>
                        <div>
                          <label className="cust-label">CITY</label>
                          {isView ? (
                            <input className="cust-input" value={formData.city || ""} disabled />
                          ) : (
                            <Select
                              options={cityOptions.map((c) => ({ value: c.name, label: c.name }))}
                              filterOption={matchFromStart}
                              value={formData.city ? { value: formData.city, label: formData.city } : null}
                              onChange={(s) => setFormData((f) => ({ ...f, city: s ? s.value : "" }))}
                              placeholder="Select city"
                              isDisabled={!formData.state}
                              isClearable
                              styles={filterSelectStyles}
                            />
                          )}
                        </div>

                        {/* Profile Image Upload */}
                        <div className="insp-profile-image-section">
                          <label className="cust-label">PROFILE IMAGE</label>
                          <div className="insp-profile-image-container">
                            <div className="insp-profile-image-wrapper">
                              {profilePreview ? (
                                <img src={profilePreview} alt="Profile" className="insp-profile-image-preview" />
                              ) : (
                                <div className="insp-profile-image-placeholder">
                                  <FiUsers size={32} />
                                </div>
                              )}
                              {!isView && (
                                <label htmlFor="insp-profile-upload" className="insp-profile-image-overlay">
                                  <FiUploadCloud size={16} />
                                  <span>UPLOAD</span>
                                </label>
                              )}
                            </div>
                            {!isView && (
                              <input
                                id="insp-profile-upload"
                                type="file"
                                accept="image/*"
                                onChange={handleImageChange}
                                style={{ display: "none" }}
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* ── Qualification Details ── */}
              <div className="sub-card" style={{ border: "1px solid #ccc" }}>
                <div className={`sub-card-header ${secQual ? "expanded" : ""}`} onClick={() => setSecQual((v) => !v)}>
                  <h2><FiAward /> QUALIFICATION DETAILS</h2>
                  {secQual ? <FiChevronUp /> : <FiChevronDown />}
                </div>
                {secQual && (
                  <div className="sub-card-content" style={{ padding: "1rem", border: "none", marginBottom: 0 }}>
                    {/* Competency */}
                    <Competency
                      value={formData.competency}
                      onChange={(val) => setFormData((f) => ({ ...f, competency: val }))}
                      readOnly={isView}
                      style={{ margin: " 2rem 0 2rem 0 " }}
                    />

                    {/* Certificates */}
                    <div style={{ padding: 0, border: "1px solid #ccc", marginBottom: "3rem" }}>
                      <div className="insp-section-sub-header">
                        <span>CERTIFICATES</span>
                        <span className="insp-cert-count">{formData.certificates.length}</span>
                      </div>

                      {formData.certificates.length > 0 && (
                        <table className="insp-cert-table">
                          <thead>
                            <tr>
                              <th>CERTIFICATE</th>
                              <th>EXPIRY DATE</th>
                              <th>FILE</th>
                              {!isView && <th style={{ width: 80 }}></th>}
                            </tr>
                          </thead>
                          <tbody>
                            {formData.certificates.map((c, idx) => {
                              const certName = certificateOptions.find((o) => o.id === c.certificateId)?.name || c.certificateId;
                              return (
                                <tr key={idx}>
                                  <td>{certName}</td>
                                  <td>{formatDate(c.expiryDate)}</td>
                                  <td>
                                    {c.certFile ? (
                                      <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#107c10" }}>
                                        <FiUploadCloud size={11} />
                                        <span title={c.certFile.name} style={{ maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.certFile.name}</span>
                                        {!isView && (
                                          <button type="button" onClick={() => handleClearCertFile(idx)} style={{ background: "none", border: "none", color: "#888", cursor: "pointer", padding: 0 }} title="Clear new file">
                                            <FiX size={11} />
                                          </button>
                                        )}
                                      </span>
                                    ) : (
                                      <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                        {c.fileUrl
                                          ? <a href={c.fileUrl} target="_blank" rel="noopener noreferrer" className="insp-file-link"><FiUpload size={11} /> View</a>
                                          : <span style={{ color: "#aaa", fontSize: 12 }}>None</span>}
                                        {!isView && (
                                          <label style={{ cursor: "pointer", fontSize: 11, color: "#1a3a6b", display: "inline-flex", alignItems: "center", gap: 4 }}>
                                            <FiUploadCloud size={11} /> {c.fileUrl ? "Replace" : "Upload"}
                                            <input type="file" accept=".pdf,.jpg,.png" style={{ display: "none" }} onChange={(e) => {
                                              const file = e.target.files?.[0];
                                              if (file) handleCertFileChange(idx, file);
                                              e.target.value = "";
                                            }} />
                                          </label>
                                        )}
                                      </span>
                                    )}
                                  </td>
                                  {!isView && (
                                    <td>
                                      <button type="button" className="cust-del-btn" onClick={() => handleRemoveCert(idx)}>
                                        <FiTrash2 size={11} /> Remove
                                      </button>
                                    </td>
                                  )}
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      )}

                      {formData.certificates.length === 0 && !certFormOpen && (
                        <div className="insp-empty-state">
                          <FiAward size={20} style={{ color: "#ccc" }} />
                          <p>No certificates added yet</p>
                          {!isView && (
                            <button type="button" style={{ marginLeft: "20px" }} className="cust-btn cust-btn-green" onClick={() => setCertFormOpen(true)}>
                              <FiPlus size={11} /> Add Certificate
                            </button>
                          )}
                        </div>
                      )}

                      {formData.certificates.length > 0 && !isView && !certFormOpen && (
                        <div style={{ padding: "10px 0" }}>
                          <button type="button" className="cust-btn cust-btn-green" onClick={() => setCertFormOpen(true)}>
                            <FiPlus size={11} /> Add Certificate
                          </button>
                        </div>
                      )}

                      {/* Inline cert add form */}
                      {certFormOpen && (
                        <div className="insp-inline-add-panel">
                          <div className="cust-panel-title">
                            <span>ADD CERTIFICATE</span>
                            <button type="button" className="cust-cancel-link" onClick={() => { setCertFormOpen(false); setCertInput(EMPTY_CERT); }}>
                              <FiX size={11} /> Cancel
                            </button>
                          </div>
                          <div className="form-grid" style={{ gridTemplateColumns: "repeat(4,1fr)", marginBottom: 10 }}>
                            <div>
                              <label className="cust-label">CERTIFICATE *</label>
                              <select
                                className="cust-input"
                                value={certInput.certificateId}
                                onChange={(e) => setCertInput((p) => ({ ...p, certificateId: e.target.value }))}
                              >
                                <option value="">-- Select --</option>
                                {certificateOptions.map((o) => (
                                  <option key={o.id} value={o.id}>{o.name}</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="cust-label">EXPIRY DATE</label>
                              <input
                                className="cust-input"
                                type="date"
                                value={certInput.expiryDate}
                                onChange={(e) => setCertInput((p) => ({ ...p, expiryDate: e.target.value }))}
                              />
                            </div>
                            <div>
                              <label className="cust-label">CERTIFICATE FILE</label>
                              <input type="file" accept=".pdf,.jpg,.png" onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) setCertInput((p) => ({ ...p, certFile: file, fileUrl: file.name }));
                              }} style={{ fontSize: 12 }} />
                            </div>
                          </div>
                          <div className="cust-panel-actions">
                            <button type="button" className="cust-btn" onClick={() => { setCertFormOpen(false); setCertInput(EMPTY_CERT); }}>Cancel</button>
                            <button type="button" className="cust-btn cust-btn-primary" onClick={handleAddCert}>
                              <FiCheck size={11} /> Save Certificate
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Client Specific Approval */}
                    <ClientSpecificApproval
                      value={formData.clientApprovals}
                      onChange={(val) => setFormData((f) => ({ ...f, clientApprovals: val }))}
                      readOnly={isView}
                    />
                  </div>
                )}
              </div>

              {/* ── Banking & Finance ── */}
              <div className="sub-card" style={{ margin: "3rem 0" }}>
                <div className={`sub-card-header ${secBank ? "expanded" : ""}`} onClick={() => setSecBank((v) => !v)}>
                  <h2><FiDollarSign /> BANKING & FINANCE
                    {formData.country && (
                      <span className="insp-bank-badge">{isIndia ? "India" : "International"}</span>
                    )}
                  </h2>
                  {secBank ? <FiChevronUp /> : <FiChevronDown />}
                </div>
                {secBank && (
                  <div className="sub-card-content">
                    {!formData.country && (
                      <div className="insp-bank-hint">Select a country in the Location section to see the relevant banking fields.</div>
                    )}

                    {/* India Banking Fields */}
                    {formData.country && isIndia && (
                      <div className="form-grid" style={{ gridTemplateColumns: "repeat(3,1fr)" }}>
                        {[
                          { field: "beneficiaryName", label: "BENEFICIARY NAME" },
                          { field: "bankName", label: "BANK NAME" },
                          { field: "branch", label: "BRANCH" },
                          { field: "bankAddress", label: "BANK ADDRESS" },
                          { field: "accountNo", label: "ACCOUNT NO." },
                          { field: "ifscCode", label: "IFSC CODE" },
                        ].map(({ field, label }) => (
                          <div key={field}>
                            <label className="cust-label">{label}</label>
                            <input
                              className="cust-input"
                              value={formData.bankIndia[field] || ""}
                              onChange={(e) => handleNestedChange("bankIndia", field, e.target.value)}
                              disabled={isView}
                              placeholder={label}
                            />
                          </div>
                        ))}
                      </div>
                    )}

                    {/* International Banking Fields */}
                    {formData.country && !isIndia && (
                      <div className="form-grid" style={{ gridTemplateColumns: "repeat(3,1fr)" }}>
                        {[
                          { field: "beneficiaryName", label: "BENEFICIARY NAME" },
                          { field: "beneficiaryAddress", label: "BENEFICIARY ADDRESS" },
                          { field: "bankName", label: "BANK NAME" },
                          { field: "bankAddress", label: "BANK ADDRESS" },
                          { field: "ibanNo", label: "IBAN NO." },
                          { field: "swiftCode", label: "SWIFT CODE" },
                        ].map(({ field, label }) => (
                          <div key={field}>
                            <label className="cust-label">{label}</label>
                            <input
                              className="cust-input"
                              value={formData.bankOther[field] || ""}
                              onChange={(e) => handleNestedChange("bankOther", field, e.target.value)}
                              disabled={isView}
                              placeholder={label}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* ── Contact Persons (Agency only) ── */}
              {isAgency && (
                <div className="sub-card" style={{ marginBottom: 0, border: "1px solid #ccc" }}>
                  <div className={`sub-card-header ${secContacts ? "expanded" : ""}`} onClick={() => setSecContacts((v) => !v)}>
                    <h2>
                      <FiUsers /> CONTACT PERSONS
                      <span className="cust-coord-count">{formData.contactPersons.length}</span>
                    </h2>
                    {secContacts ? <FiChevronUp /> : <FiChevronDown />}
                  </div>

                  {secContacts && (
                    <div>
                      {formData.contactPersons.length === 0 && !contactFormOpen && (
                        <div className="cust-coord-empty">
                          <div className="cust-coord-empty-icon"><FiUsers size={16} /></div>
                          <p className="cust-coord-empty-text">No contact persons added yet</p>
                          {!isView && (
                            <button type="button" className="cust-btn cust-btn-green" onClick={() => setContactFormOpen(true)}>
                              <FiPlus size={11} /> Add Contact Person
                            </button>
                          )}
                        </div>
                      )}

                      {formData.contactPersons.length > 0 && (
                        <>
                          <table className="cust-coord-table">
                            <thead>
                              <tr>
                                <th style={{ width: "30%" }}>NAME</th>
                                <th style={{ width: "36%" }}>EMAIL</th>
                                <th style={{ width: "24%" }}>MOBILE</th>
                                {!isView && <th style={{ width: "10%" }}></th>}
                              </tr>
                            </thead>
                            <tbody>
                              {formData.contactPersons.map((c, idx) => (
                                <tr key={idx}>
                                  <td>
                                    <div className="cust-nc">
                                      <div className="cust-avatar">{initials(c.personName)}</div>
                                      <span className="cust-name">{c.personName}</span>
                                    </div>
                                  </td>
                                  <td className="cust-td-muted">{c.email}</td>
                                  <td className="cust-td-muted">{c.mobileNumber || "—"}</td>
                                  {!isView && (
                                    <td>
                                      <button type="button" className="cust-del-btn" onClick={() => handleRemoveContact(idx)}>
                                        <FiTrash2 size={12} /> Remove
                                      </button>
                                    </td>
                                  )}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          {!isView && !contactFormOpen && (
                            <div className="cust-coord-footer">
                              <button type="button" className="cust-btn cust-btn-green" onClick={() => setContactFormOpen(true)}>
                                <FiPlus size={11} /> Add Contact Person
                              </button>
                            </div>
                          )}
                        </>
                      )}

                      {contactFormOpen && (
                        <div className="cust-coord-add-panel">
                          <div className="cust-panel-title">
                            <span>NEW CONTACT PERSON</span>
                            <button type="button" className="cust-cancel-link" onClick={() => { setContactFormOpen(false); setContactInput(EMPTY_CONTACT); }}>
                              <FiX size={11} /> Cancel
                            </button>
                          </div>
                          <div className="form-grid" style={{ marginBottom: 10 }}>
                            <div>
                              <label className="cust-label">NAME *</label>
                              <input
                                className="cust-input"
                                value={contactInput.personName}
                                onChange={(e) => setContactInput((p) => ({ ...p, personName: e.target.value }))}
                                placeholder="Jane Smith"
                                onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
                              />
                            </div>
                            <div>
                              <label className="cust-label">EMAIL *</label>
                              <input
                                className="cust-input"
                                type="email"
                                value={contactInput.email}
                                onChange={(e) => setContactInput((p) => ({ ...p, email: e.target.value }))}
                                placeholder="jane@agency.com"
                                onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
                              />
                            </div>
                            <div>
                              <PhoneField
                                label="MOBILE"
                                value={contactInput.mobileNumber}
                                onChange={(val) => setContactInput((p) => ({ ...p, mobileNumber: val }))}
                                placeholder="Enter mobile number"
                              />
                            </div>
                          </div>
                          <div className="cust-panel-actions">
                            <button type="button" className="cust-btn" onClick={() => { setContactFormOpen(false); setContactInput(EMPTY_CONTACT); }}>Cancel</button>
                            <button type="button" className="cust-btn cust-btn-primary" onClick={handleAddContact}>
                              <FiCheck size={11} /> Save Contact
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

            </div>{/* end modal body */}

            {/* Modal Footer */}
            {!isView ? (
              <div className="cust-modal-footer">
                {(isAdd || modal === "edit") && user?.role === 'global_superadmin' && (
                  <div style={{ marginRight: "auto", display: "flex", alignItems: "center", gap: 8 }}>
                    <label className="cust-label" style={{ margin: 0 }}>CREATED BY</label>
                    <select className="cust-input" style={{ width: 180 }} name="createdBy" value={formData.createdBy} onChange={handleFormChange}>
                      <option value="">Myself</option>
                      {creators.map((c) => (
                        <option key={c.id} value={c.id}>{c.fullName}</option>
                      ))}
                    </select>
                  </div>
                )}
                <button type="button" className="cust-btn" onClick={requestCloseModal}>Cancel</button>
                <button type="button" className="cust-btn cust-btn-primary" onClick={handleSave} disabled={saving}>
                  <FiCheck size={11} /> {saving ? "Saving…" : "Save Inspector"}
                </button>
              </div>
            ) : (
              <div className="cust-modal-footer">
                <button type="button" className="cust-btn" onClick={closeModal}>Close</button>
              </div>
            )}

          </div>
        </div>
      )}

      {/* ── Bulk Upload Modal ── */}
      <MasterUploadModal
        isOpen={isUploadOpen}
        onClose={() => setIsUploadOpen(false)}
        title="Upload Inspectors"
        templateUrl={`${API_URLS.INSPECTORS}/template`}
        uploadUrl={`${API_URLS.INSPECTORS}/upload`}
        instructions={[
          "Inspector Name, Email, and Country are mandatory fields.",
          "Email must be unique and a valid email format.",
          "Inspector Type must be either 'Direct' or 'Agency'.",
        ]}
        onSuccess={fetchRows}
        token={token}
      />
    </div>
  );
};

export default Inspectors;
